import React from 'react';
import { useApp } from '../../context/AppContext';
import {
  BarChart3,
  CheckCircle2,
  XCircle,
  HelpCircle,
  Clock,
  BookOpen,
  ArrowRight,
  TrendingUp,
  Award,
  ChevronRight,
  PieChart,
} from 'lucide-react';

export const AnalyticsView: React.FC = () => {
  const {
    currentUser,
    subjects,
    attempts,
    setCurrentView,
    setLatestAttempt,
  } = useApp();

  const myAttempts = currentUser ? attempts.filter((a) => a.user_id === currentUser.id) : [];

  // Overall totals
  const totalTests = myAttempts.length;
  const totalQuestionsAnswered = myAttempts.reduce((acc, a) => acc + (a.correct_answers + a.wrong_answers), 0);
  const totalCorrect = myAttempts.reduce((acc, a) => acc + a.correct_answers, 0);
  const totalWrong = myAttempts.reduce((acc, a) => acc + a.wrong_answers, 0);
  const totalSkipped = myAttempts.reduce((acc, a) => acc + a.skipped_answers, 0);
  const totalTimeSeconds = myAttempts.reduce((acc, a) => acc + a.time_spent_seconds, 0);
  const avgAccuracy = totalQuestionsAnswered > 0 ? Math.round((totalCorrect / totalQuestionsAnswered) * 100) : 0;

  // Subject-wise stats
  const subjectStats = subjects.map((subj) => {
    const subjAttempts = myAttempts.filter((a) => a.subject_name === subj.name);
    const solved = subjAttempts.reduce((acc, a) => acc + (a.correct_answers + a.wrong_answers), 0);
    const correct = subjAttempts.reduce((acc, a) => acc + a.correct_answers, 0);
    const accPct = solved > 0 ? Math.round((correct / solved) * 100) : 0;
    return {
      subject: subj,
      attemptsCount: subjAttempts.length,
      solved,
      correct,
      accPct,
    };
  });

  const handleReviewAttempt = (attemptId: number) => {
    const attempt = attempts.find((a) => a.id === attemptId);
    if (attempt) {
      setLatestAttempt(attempt);
      setCurrentView('quiz-scorecard');
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6 sm:space-y-8 pb-24 md:pb-16">
      {/* 1. Header */}
      <div>
        <h1 className="text-2xl font-extrabold text-[#111111] tracking-tight">Performance Analytics</h1>
        <p className="text-xs sm:text-sm text-[#5F6368] mt-0.5">
          Detailed metrics on your question-solving trends, accuracy by subject, and historical exam attempts.
        </p>
      </div>

      {/* 2. Summary KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3.5 sm:gap-4">
        <div className="bg-white p-5 rounded-2xl border border-[#E5E9E9] shadow-[0_2px_8px_rgba(17,17,17,0.02)]">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-[#5F6368] uppercase tracking-wider">
              Overall Accuracy
            </span>
            <div className="w-8 h-8 rounded-xl bg-[#EAF8F1] text-[#20A66A] flex items-center justify-center border border-[#20A66A]/20">
              <TrendingUp className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black text-[#111111]">{avgAccuracy}%</div>
          <p className="text-xs text-[#5F6368] mt-1">Across all subjects</p>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-[#E5E9E9] shadow-[0_2px_8px_rgba(17,17,17,0.02)]">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-[#5F6368] uppercase tracking-wider">
              Total Solved
            </span>
            <div className="w-8 h-8 rounded-xl bg-[#E8FAF9] text-[#1b8e88] flex items-center justify-center border border-[#56E8E1]/30">
              <CheckCircle2 className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black text-[#111111]">{totalQuestionsAnswered}</div>
          <p className="text-xs text-[#5F6368] mt-1">{totalCorrect} correct answers</p>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-[#E5E9E9] shadow-[0_2px_8px_rgba(17,17,17,0.02)]">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-[#5F6368] uppercase tracking-wider">
              Sessions Completed
            </span>
            <div className="w-8 h-8 rounded-xl bg-[#FFF7D9] text-[#8a6508] flex items-center justify-center border border-[#EBC138]/40">
              <BookOpen className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black text-[#111111]">{totalTests}</div>
          <p className="text-xs text-[#5F6368] mt-1">Tests & revisions</p>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-[#E5E9E9] shadow-[0_2px_8px_rgba(17,17,17,0.02)]">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-[#5F6368] uppercase tracking-wider">
              Practice Time
            </span>
            <div className="w-8 h-8 rounded-xl bg-[#E8FAF9] text-[#1b8e88] flex items-center justify-center border border-[#56E8E1]/30">
              <Clock className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black text-[#111111]">
            {Math.floor(totalTimeSeconds / 60)}m
          </div>
          <p className="text-xs text-[#5F6368] mt-1">Total time invested</p>
        </div>
      </div>

      {/* 3. Accuracy Breakdown Bar */}
      {totalQuestionsAnswered > 0 && (
        <div className="bg-white rounded-3xl border border-[#E5E9E9] p-5 sm:p-6 shadow-[0_4px_20px_rgba(17,17,17,0.02)] space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold text-[#111111] uppercase tracking-wider">
              Answer Distribution ({totalQuestionsAnswered} MCQs)
            </h2>
            <div className="flex items-center gap-4 text-xs font-semibold">
              <span className="flex items-center gap-1.5 text-[#20A66A]">
                <span className="w-2.5 h-2.5 rounded-full bg-[#20A66A]" />
                Correct ({totalCorrect})
              </span>
              <span className="flex items-center gap-1.5 text-[#D94A5C]">
                <span className="w-2.5 h-2.5 rounded-full bg-[#D94A5C]" />
                Wrong ({totalWrong})
              </span>
              <span className="flex items-center gap-1.5 text-[#5F6368]">
                <span className="w-2.5 h-2.5 rounded-full bg-slate-300" />
                Skipped ({totalSkipped})
              </span>
            </div>
          </div>

          <div className="w-full h-3.5 bg-[#F0F4F4] rounded-full overflow-hidden flex">
            <div
              className="bg-[#20A66A] h-full transition-all"
              style={{ width: `${(totalCorrect / totalQuestionsAnswered) * 100}%` }}
              title={`Correct: ${totalCorrect}`}
            />
            <div
              className="bg-[#D94A5C] h-full transition-all"
              style={{ width: `${(totalWrong / totalQuestionsAnswered) * 100}%` }}
              title={`Wrong: ${totalWrong}`}
            />
            <div
              className="bg-slate-300 h-full transition-all"
              style={{ width: `${(totalSkipped / totalQuestionsAnswered) * 100}%` }}
              title={`Skipped: ${totalSkipped}`}
            />
          </div>
        </div>
      )}

      {/* 4. Subject-Wise Accuracy Breakdown */}
      <div className="bg-white rounded-3xl border border-[#E5E9E9] p-5 sm:p-6 space-y-5 shadow-[0_4px_20px_rgba(17,17,17,0.02)]">
        <div>
          <h2 className="text-lg font-bold text-[#111111] tracking-tight">Subject-Wise Performance</h2>
          <p className="text-xs text-[#5F6368]">
            Identify your strengths and pinpoint topics that require targeted question practice.
          </p>
        </div>

        <div className="space-y-3.5">
          {subjectStats.map(({ subject, solved, correct, accPct, attemptsCount }) => (
            <div
              key={subject.id}
              className="p-4 rounded-2xl bg-[#F7FAFA] border border-[#E5E9E9] flex flex-col sm:flex-row sm:items-center justify-between gap-3"
            >
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-[#111111]">{subject.name}</span>
                    <span className="text-xs text-[#5F6368]">
                      • {attemptsCount} sessions ({solved} questions)
                    </span>
                  </div>
                  <div className="text-xs font-bold text-[#111111]">
                    {solved > 0 ? `${accPct}% Accuracy` : 'Not practiced yet'}
                  </div>
                </div>

                <div className="w-full bg-[#E5E9E9] rounded-full h-2 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      accPct >= 70
                        ? 'bg-[#20A66A]'
                        : accPct >= 50
                        ? 'bg-[#56E8E1]'
                        : 'bg-[#D94A5C]'
                    }`}
                    style={{ width: `${Math.max(solved > 0 ? 5 : 0, accPct)}%` }}
                  />
                </div>
              </div>

              {solved > 0 && (
                <div className="shrink-0 text-right">
                  <span
                    className={`inline-flex px-2.5 py-1 rounded-lg text-xs font-bold ${
                      accPct >= 70
                        ? 'bg-[#EAF8F1] text-[#20A66A]'
                        : accPct >= 50
                        ? 'bg-[#E8FAF9] text-[#1b8e88]'
                        : 'bg-[#FDEDEF] text-[#D94A5C]'
                    }`}
                  >
                    {correct} / {solved} Correct
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* 5. Historical Exam & Practice Log */}
      <div className="bg-white rounded-3xl border border-[#E5E9E9] p-5 sm:p-6 shadow-[0_4px_20px_rgba(17,17,17,0.02)] space-y-4">
        <div>
          <h2 className="text-lg font-bold text-[#111111] tracking-tight">Full Attempt History</h2>
          <p className="text-xs text-[#5F6368]">
            Click any row to open the complete question-by-question breakdown and step-by-step explanations.
          </p>
        </div>

        {myAttempts.length === 0 ? (
          <div className="text-center py-12 text-[#5F6368] text-xs">
            No completed tests yet. Start with a Revision Mode practice set from the dashboard!
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-[#F7FAFA] text-[#5F6368] border-b border-[#E5E9E9]">
                <tr>
                  <th className="py-2.5 px-3.5 font-bold uppercase tracking-wider text-[10px]">Question Set</th>
                  <th className="py-2.5 px-3.5 font-bold uppercase tracking-wider text-[10px]">Subject</th>
                  <th className="py-2.5 px-3.5 font-bold uppercase tracking-wider text-[10px]">Mode</th>
                  <th className="py-2.5 px-3.5 font-bold uppercase tracking-wider text-[10px]">Score</th>
                  <th className="py-2.5 px-3.5 font-bold uppercase tracking-wider text-[10px]">Breakdown</th>
                  <th className="py-2.5 px-3.5 font-bold uppercase tracking-wider text-[10px]">Time</th>
                  <th className="py-2.5 px-3.5 font-bold uppercase tracking-wider text-[10px]">Date</th>
                  <th className="py-2.5 px-3.5 font-bold uppercase tracking-wider text-[10px] text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E9E9] text-[#111111]">
                {myAttempts.map((att) => (
                  <tr key={att.id} className="hover:bg-[#F7FAFA] transition-colors">
                    <td className="py-3 px-3.5 font-bold text-[#111111]">
                      {att.question_set_name || 'Practice Set'}
                    </td>
                    <td className="py-3 px-3.5 text-[#5F6368]">{att.subject_name || 'General'}</td>
                    <td className="py-3 px-3.5">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full font-bold uppercase text-[10px] ${
                          att.mode === 'revision'
                            ? 'bg-[#E8FAF9] text-[#1b8e88] border border-[#56E8E1]/40'
                            : 'bg-[#FFF7D9] text-[#8a6508] border border-[#EBC138]/40'
                        }`}
                      >
                        {att.mode}
                      </span>
                    </td>
                    <td className="py-3 px-3.5 font-black text-[#111111]">
                      <span
                        className={
                          att.score_percentage >= 70
                            ? 'text-[#20A66A]'
                            : att.score_percentage >= 50
                            ? 'text-[#8a6508]'
                            : 'text-[#D94A5C]'
                        }
                      >
                        {att.score_percentage}%
                      </span>
                    </td>
                    <td className="py-3 px-3.5 text-[#5F6368]">
                      <span className="text-[#20A66A] font-bold">{att.correct_answers}C</span> /{' '}
                      <span className="text-[#D94A5C] font-bold">{att.wrong_answers}W</span> /{' '}
                      <span className="text-[#5F6368]">{att.skipped_answers}S</span>
                    </td>
                    <td className="py-3 px-3.5 text-[#5F6368]">
                      {Math.floor(att.time_spent_seconds / 60)}m {att.time_spent_seconds % 60}s
                    </td>
                    <td className="py-3 px-3.5 text-[#5F6368]">
                      {new Date(att.completed_at).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                    <td className="py-3 px-3.5 text-right">
                      <button
                        onClick={() => handleReviewAttempt(att.id)}
                        className="px-2.5 py-1 text-xs font-bold rounded-lg bg-[#E8FAF9] hover:bg-[#56E8E1]/30 text-[#1b8e88] border border-[#56E8E1]/30 transition-colors cursor-pointer"
                      >
                        Review
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsView;
